import { get, set, del, keys } from 'idb-keyval';
import { DocumentModel } from '../ast/document.types';
import { migrateDocument } from '../ast/migration';

const DOC_PREFIX = 'sds_doc_';
const ACTIVE_DOC_KEY = 'sds_active_doc_id';

/**
 * Persists a document model to local IndexedDB
 */
export async function saveDocumentToDb(doc: DocumentModel): Promise<void> {
  try {
    const key = `${DOC_PREFIX}${doc.metadata.id}`;
    await set(key, JSON.stringify(doc));
    await set(ACTIVE_DOC_KEY, doc.metadata.id);
  } catch (error) {
    console.error('Failed to save document to IndexedDB:', error);
    throw error;
  }
}

/**
 * Loads a document by ID from IndexedDB
 */
export async function loadDocumentFromDb(id: string): Promise<DocumentModel | null> {
  try {
    const key = `${DOC_PREFIX}${id}`;
    const raw = await get<string>(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return migrateDocument(parsed);
  } catch (error) {
    console.error(`Failed to load document "${id}" from IndexedDB:`, error);
    return null;
  }
}

/**
 * Gets the active or most recently edited document ID
 */
export async function getActiveDocumentIdFromDb(): Promise<string | null> {
  try {
    return (await get<string>(ACTIVE_DOC_KEY)) || null;
  } catch {
    return null;
  }
}

/**
 * Lists all locally saved document headers
 */
export async function listSavedDocumentsFromDb(): Promise<Array<{ id: string; title: string; modifiedAt: string }>> {
  try {
    const allKeys = await keys();
    const docKeys = allKeys.filter((k) => typeof k === 'string' && k.startsWith(DOC_PREFIX)) as string[];
    const result: Array<{ id: string; title: string; modifiedAt: string }> = [];

    for (const k of docKeys) {
      const raw = await get<string>(k);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          result.push({
            id: parsed.metadata?.id || k.replace(DOC_PREFIX, ''),
            title: parsed.metadata?.title || 'Untitled Document',
            modifiedAt: parsed.metadata?.modifiedAt || '',
          });
        } catch {
          // Ignore corrupted entries
        }
      }
    }

    return result;
  } catch (error) {
    console.error('Failed to list documents from IndexedDB:', error);
    return [];
  }
}

/**
 * Deletes a document from IndexedDB
 */
export async function deleteDocumentFromDb(id: string): Promise<void> {
  try {
    await del(`${DOC_PREFIX}${id}`);
  } catch (error) {
    console.error(`Failed to delete document "${id}" from IndexedDB:`, error);
  }
}
